import React from "react";
import { Navigate, useLocation } from "react-router-dom";

import { ROUTES, STORAGE_KEYS, UserInfo, getUserInfo, setUserInfo } from "./common";
import { signOut } from "./api-service/auth";
import { ErrorDialog } from "./parts/app-dialog";
import { Loading } from "./parts/loading";
import messages from "./messages/ja.json";

import "./styles/auth-flow.css";

type FaceStatus = "unknown" | "registered" | "unregistered" | "error";
// Reuses the same shape as FaceStatus for the secret-question enrollment gate.
type SecretStatus = "unknown" | "registered" | "unregistered" | "error";

interface AuthContextType {
  user: UserInfo | null;
  faceStatus: FaceStatus;
  secretStatus: SecretStatus;
  // Persist + sync the signed-in user captured at login (the app's auth signal).
  // faceStatus comes from the login challenge: FACE_MFA / NEW_PASSWORD_REQUIRED
  // (after change-password) -> registered; FACE_REGISTRATION / FIRST_TIME_MFA
  // (after change-password) -> unregistered.
  // secretStatus comes from the backend's secret_questions_registered flag on login /
  // change-password (or the SECRET_QUESTION_REQUIRED challenge) — see those pages.
  signin: (
    user: UserInfo,
    faceStatus?: FaceStatus,
    secretStatus?: SecretStatus,
    callback?: VoidFunction
  ) => void;
  signout: (callback?: VoidFunction) => void;
  // Update the face-registration status (after register/unregister) for the gate.
  setFaceStatus: (value: FaceStatus) => void;
  // Update the secret-question status (after enroll) for the gate.
  setSecretStatus: (value: SecretStatus) => void;
}

const AuthContext = React.createContext<AuthContextType>(null!);

// Restore the face gate on reload: a signed-in user keeps their persisted status; legacy
// sessions with no saved value are assumed registered (they already got into the app).
function initialFaceStatus(): FaceStatus {
  if (!getUserInfo()) return "unknown";
  return (localStorage.getItem(STORAGE_KEYS.faceStatus) as FaceStatus | null) ?? "registered";
}

// Same reload logic as the face gate: legacy sessions with no saved value are assumed
// registered (they already got into the app before this feature existed).
function initialSecretStatus(): SecretStatus {
  if (!getUserInfo()) return "unknown";
  return (localStorage.getItem(STORAGE_KEYS.secretStatus) as SecretStatus | null) ?? "registered";
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<UserInfo | null>(getUserInfo());
  const [faceStatus, setFaceStatusState] = React.useState<FaceStatus>(initialFaceStatus);
  const [secretStatus, setSecretStatusState] = React.useState<SecretStatus>(initialSecretStatus);

  // Persist alongside state so the gate survives a reload (e.g. reloading on
  // /face-registration before the face is registered must not fall through to /checkin).
  const setFaceStatus = (value: FaceStatus) => {
    localStorage.setItem(STORAGE_KEYS.faceStatus, value);
    setFaceStatusState(value);
  };

  const setSecretStatus = (value: SecretStatus) => {
    localStorage.setItem(STORAGE_KEYS.secretStatus, value);
    setSecretStatusState(value);
  };

  const signin = (
    nextUser: UserInfo,
    nextFaceStatus: FaceStatus = "registered",
    nextSecretStatus: SecretStatus = "registered",
    callback?: VoidFunction
  ) => {
    setUserInfo(nextUser); // persist so it survives reload
    setUser(nextUser);
    setFaceStatus(nextFaceStatus); // persists faceStatus too
    setSecretStatus(nextSecretStatus); // persists secretStatus too
    callback?.();
  };

  const signout = (callback?: VoidFunction) => {
    signOut(); // clearSession() also drops the persisted face/secret status
    setUser(null);
    setFaceStatusState("unknown");
    setSecretStatusState("unknown");
    callback?.();
  };

  const value = { user, faceStatus, secretStatus, signin, signout, setFaceStatus, setSecretStatus };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return React.useContext(AuthContext);
}

// Route guard: not signed in -> redirect to /login (preserving `from`).
export function RequireAuth({ children }: { children: React.ReactElement }) {
  const auth = useAuth();
  const location = useLocation();
  if (!auth.user) {
    return <Navigate to={ROUTES.login} state={{ from: location }} replace />;
  }
  return children;
}

// Face-registration gate (Issue ①): faceStatus is driven by the login/register flow
// (set via auth.setFaceStatus), so the gate survives drop-off without a "first time" flag.
export function RequireFaceRegistered({ children }: { children: React.ReactElement }) {
  const auth = useAuth();
  // Set once the user acknowledges the status-check error. The status is only ever resolved
  // by the login flow, so restarting there is what actually retries it — flipping the status
  // back to "unknown" left the gate spinning on <Loading/> forever, since nothing re-runs
  // the check in place.
  const [restart, setRestart] = React.useState(false);
  if (restart) {
    return <Navigate to={ROUTES.login} replace />;
  }
  if (auth.faceStatus === "unknown") {
    return <Loading />;
  }
  // The status check failed, so the gate can't tell whether the face is registered. Shown
  // as the エラー modal over the app background; the ✕ and Escape do the same thing as the
  // button, because restarting the flow is the only way forward from here.
  if (auth.faceStatus === "error") {
    return (
      <div className="app-shell">
        <ErrorDialog
          open
          // statusError already ends in "…再試行してください。", so the retry copy is not
          // repeated in the body — it stays where it is actionable, on the button.
          message={messages.face.statusError}
          actionLabel={messages.face.statusRetry}
          // signout first: the login form must not be shown while the session (and the
          // failed status persisted next to it) is still around — otherwise leaving the
          // form and reopening any route walks straight back into this modal.
          onClose={() => {
            auth.signout();
            setRestart(true);
          }}
        />
      </div>
    );
  }
  if (auth.faceStatus === "unregistered") {
    return <Navigate to={ROUTES.faceRegistration} replace />;
  }
  return children;
}

// Secret-question enrollment gate (FE-1): mirrors RequireFaceRegistered. If enrollment is
// skipped or interrupted, the gate re-redirects to /secret-questions on the next app load
// until secretStatus === "registered" (Q&A #17 "Resume").
export function RequireSecretQuestionsRegistered({ children }: { children: React.ReactElement }) {
  const auth = useAuth();
  // Same shape as the face gate above, including the fix for its retry: the old handler set
  // the status back to "unknown" and left the screen on an endless 読み込み中… .
  const [restart, setRestart] = React.useState(false);
  if (restart) {
    return <Navigate to={ROUTES.login} replace />;
  }
  if (auth.secretStatus === "unknown") {
    return <Loading />;
  }
  if (auth.secretStatus === "error") {
    return (
      <div className="app-shell">
        <ErrorDialog
          open
          message={messages.secretQuestions.statusError}
          actionLabel={messages.secretQuestions.statusRetry}
          onClose={() => {
            auth.signout();
            setRestart(true);
          }}
        />
      </div>
    );
  }
  if (auth.secretStatus === "unregistered") {
    return <Navigate to={ROUTES.secretQuestions} replace />;
  }
  return children;
}

export { getUserInfo };
